import React from 'react';
import { Link } from 'react-router-dom';
import { Header, Footer } from '../../components';
import { PageTitle } from '../../components/Text';
import './IntroductionScreen.scss';

const IntroductionScreen = () => (
  <>
    <Header />
    <div id="main">
      <div className="container">
        <div className="content-wrap introduction">
          <PageTitle className="project-title">
            Welcome to the online form to renew a Blue CPCS Competent Operator
            Card via logbook
          </PageTitle>
          <div className="primary-content">
            <p>
              Before proceeding with the application, the applicant{' '}
              <strong>MUST have</strong> achieved the following Renewal
              Requirements:
            </p>
            <ul>
              <li>
                The relevant CITB Health, Safety, and Environment (HS&E) test
                (unless you have already achieved the HS&E test within the last
                two years).
                <p>
                  If you are unsure as to which HS&E test to take, please look
                  at the{' '}
                  <a href="https://cpcs.nocn.org.uk/help-and-support/cpcs-faqs/">
                    CPCS FAQ&apos;s
                  </a>
                </p>
                <p>
                  If the HS&E has not been achieved within 2 years, please book
                  online at{' '}
                  <a href="https://www.citb.co.uk/courses-and-qualifications/hse-test-and-cards/book-a-test/">
                    CITB Health, Safety & Environment Booking Page
                  </a>{' '}
                  or contact Pearson Vue by calling{' '}
                  <strong>0344 994 4488</strong>
                </p>
              </li>
              <li>
                The relevant CPCS Renewal Test (RT) (unless the Renewal Test has
                been achieved within the last two years) or the relevant CPCS
                Renewal CPD Course. You may be exempt from taking the renewal
                test for certain categories if you added a category to your
                Competent Operator card two years before your expiry date.
                <p>
                  To find out which Renewal Course to book, please use the{' '}
                  <a href="https://cpcs.nocn.org.uk/help-and-support/renewal-test-tool/">
                    Renewal Test Tool
                  </a>{' '}
                  and select the relevant categories you would like to renew.
                </p>
              </li>
              <li>
                Completed at least one of the following:
                <ul>
                  <li>
                    <a href="https://cpcs.nocn.org.uk/data/General_Downloads/NOCN-CPCSA5120ppLogbookAW(SPREADS).pdf">
                      CPCS Logbook
                    </a>{' '}
                    (have 300 hours completed per category — this is not
                    required for categories that have been added to the card
                    within the last two years) or
                  </li>
                  <li>
                    <a href="https://cpcs.nocn.org.uk/help-and-support/cpcs-support-documents/operator/">
                      Competency Assessments
                    </a>{' '}
                    (only applicable to categories A61/A62/A68 — upload copies
                    with application)
                  </li>
                </ul>
              </li>
            </ul>

            <p>
              If you are renewing via On-Site Assessment (OSA) or CPCS Practical
              Test (PT) these can be arranged through your local CPCS Test
              Centre,{' '}
              <a href="https://testcentre-search.nocn.org/">
                https://testcentre-search.nocn.org/
              </a>
              . Once completed the Test Centre will apply for the new blue CPCS
              card.
            </p>

            <p>
              <strong>Industry Accreditation Withdrawal</strong>
            </p>

            <p>
              Due to the requirements of the Construction Leadership Council,
              CPCS is required to phase out the CPCS Competent Operator Card
              (blue cards) gained through Industry accreditation by the 31st of
              December 2024. If you have gained your blue card under industry
              accreditation and you have not previously achieved a VQ in certain
              Construction plant-related occupations, you will have to achieve
              an NOCN CPCS Competence Interview by the 31st of December 2024. If
              you hold evidence of a previous VQ containing the following Units:
              <ul>
                <li>
                  Conforming to General Health, Safety and Welfare in the
                  Workplace
                </li>
                <li>
                  Conforming to Productive Working Practices in the Workplace
                </li>
              </ul>
              (Or the equivalent wording on your VQ cert relating to your
              Awarding Organisation), please attach this with your application.
            </p>

            <p>
              If copies of the VQ Certificate are supplied after the reduced
              validation period card has been issued and a full-term card is
              issued, a £10 fee is payable.
            </p>

            <p>
              If you don’t hold the VQ with the units above, you will be issued
              a reduced validation period card on renewal with a fixed expiry
              date of 31st December 2024. If you do not hold a VQ, the
              requirement is to sit a CPCS Competency Interview with a CPCS Test
              centre to top your card up for the full 5 years.
            </p>

            <p>
              You can complete the NOCN CPCS Competence Interview at any{' '}
              <a href="https://cpcs.nocn.org.uk/centre-search/">
                CPCS Test Centre
              </a>{' '}
              and the centre can charge a maximum fee of £160.00. The Competence
              Interview will be notified to the CPCS team. The Test Centre will
              apply for your card.
            </p>

            <p>For Example,</p>
            <ol>
              <li>Blue card expired 30/06/2022 NO VQ Achieved</li>
              <li>Issued with Industry accredited card till 31/12/2024</li>
              <li>Achieves Competency Interview</li>
              <li>
                Card topped up to full 5 years from expiry date 30/06/2027.
              </li>
            </ol>

            <p>
              Please visit{' '}
              <a href="https://cpcs.nocn.org.uk/CPCS/cpcs-blue-competent-operator-card/industry-accreditation-withdrawal/">
                https://cpcs.nocn.org.uk/CPCS/cpcs-blue-competent-operator-card/industry-accreditation-withdrawal/
              </a>{' '}
              for further information
            </p>

            <p>
              If you require help completing this form, please contact CPCS on
              0300 999 1177 opt 1, opt 1 or email{' '}
              <a href="mailto:CPCS@nocn.org.uk">CPCS@nocn.org.uk</a>
            </p>

            <p>
              Alternatively you can also contact us via the webchat service{' '}
              <a href="https://cpcs.nocn.org.uk/contact/">
                https://cpcs.nocn.org.uk/contact
              </a>
            </p>

            <p className="introduction__information-paragraph">
              <strong>
                If the applicant has met all the renewal requirements as above,
                please proceed with this application.
              </strong>
            </p>

            <button
              className="button introduction__action-button"
              type="button"
            >
              <Link to="/qualification" className="link">
                Proceed to application
              </Link>
            </button>
          </div>
        </div>
      </div>
    </div>
    <Footer />
  </>
);

export default IntroductionScreen;
